import { cn } from '@/lib/utils';
import type { WorkspaceFile } from '@/types';
import { File, FileCode, FileText } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/* ================================================================== */
/*  Types                                                             */
/* ================================================================== */

interface FlatFile {
  path: string;
  name: string;
  directory: string;
}

interface QuickFileOpenProps {
  isOpen: boolean;
  onClose: () => void;
  fileTree: WorkspaceFile | null;
  onSelect: (path: string) => void;
}

/* ================================================================== */
/*  Helpers                                                           */
/* ================================================================== */

/** Flatten the workspace file tree into a searchable list of file paths */
function flattenTree(node: WorkspaceFile | null): FlatFile[] {
  if (!node) return [];

  const result: FlatFile[] = [];

  function walk(n: WorkspaceFile) {
    if (n.type === 'file') {
      const lastSlash = n.path.lastIndexOf('/');
      result.push({
        path: n.path,
        name: n.name,
        directory: lastSlash > 0 ? n.path.slice(0, lastSlash) : '',
      });
    }
    if (n.children) {
      for (const child of n.children) walk(child);
    }
  }

  walk(node);
  return result.sort((a, b) => a.path.localeCompare(b.path));
}

/** Fuzzy-match score: higher = better match. 0 = no match. */
function fuzzyScore(query: string, target: string): number {
  const q = query.toLowerCase();
  const t = target.toLowerCase();

  // Exact include
  if (t === q) return 200;
  if (t.startsWith(q)) return 150;

  // Filename contains query
  const lastSlash = t.lastIndexOf('/');
  const filename = lastSlash >= 0 ? t.slice(lastSlash + 1) : t;
  if (filename.startsWith(q)) return 130;
  if (filename.includes(q)) return 110;

  // Full path contains query
  if (t.includes(q)) return 80;

  // Fuzzy: all chars in order
  let qi = 0;
  let consecutive = 0;
  let maxConsecutive = 0;
  for (let i = 0; i < t.length && qi < q.length; i++) {
    if (t[i] === q[qi]) {
      qi++;
      consecutive++;
      maxConsecutive = Math.max(maxConsecutive, consecutive);
    } else {
      consecutive = 0;
    }
  }

  if (qi === q.length) return 20 + maxConsecutive * 5;
  return 0;
}

/** Get file icon based on extension */
function getFileIcon(name: string) {
  const ext = name.split('.').pop()?.toLowerCase();
  switch (ext) {
    case 'sol':
    case 'ts':
    case 'tsx':
    case 'js':
    case 'jsx':
      return FileCode;
    case 'md':
    case 'txt':
    case 'json':
    case 'yaml':
    case 'yml':
    case 'toml':
      return FileText;
    default:
      return File;
  }
}

/** Get language color hint */
function getExtColor(name: string): string {
  const ext = name.split('.').pop()?.toLowerCase();
  switch (ext) {
    case 'sol':
      return 'text-purple-400';
    case 'ts':
    case 'tsx':
      return 'text-blue-400';
    case 'js':
    case 'jsx':
      return 'text-yellow-400';
    case 'json':
      return 'text-green-400';
    case 'md':
      return 'text-gray-400';
    default:
      return 'text-[#858585]';
  }
}

/* ================================================================== */
/*  Component                                                         */
/* ================================================================== */

export default function QuickFileOpen({ isOpen, onClose, fileTree, onSelect }: QuickFileOpenProps) {
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Flatten file tree once
  const allFiles = useMemo(() => flattenTree(fileTree), [fileTree]);

  // Focus on open
  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setSelectedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  // Filter and score
  const filtered = useMemo(() => {
    if (!query.trim()) return allFiles.slice(0, 50); // Show first 50 when empty
    const q = query.trim();
    return allFiles
      .map((f) => ({ file: f, score: fuzzyScore(q, f.path) }))
      .filter((x) => x.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 50)
      .map((x) => x.file);
  }, [query, allFiles]);

  // Keep selection in bounds
  useEffect(() => {
    if (selectedIndex >= filtered.length) {
      setSelectedIndex(Math.max(0, filtered.length - 1));
    }
  }, [filtered.length, selectedIndex]);

  // Scroll selected into view
  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-idx="${selectedIndex}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [selectedIndex]);

  const handleSelect = useCallback(
    (file: FlatFile) => {
      onClose();
      setTimeout(() => onSelect(file.path), 50);
    },
    [onClose, onSelect],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex((i) => Math.min(i + 1, filtered.length - 1));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex((i) => Math.max(i - 1, 0));
          break;
        case 'Enter':
          e.preventDefault();
          if (filtered[selectedIndex]) handleSelect(filtered[selectedIndex]);
          break;
        case 'Escape':
          e.preventDefault();
          onClose();
          break;
      }
    },
    [filtered, selectedIndex, handleSelect, onClose],
  );

  // Global escape listener
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center pt-[12vh]"
      onClick={onClose}
    >
      <div
        className="w-[560px] max-h-[420px] bg-[#252526] rounded-lg border border-[#454545] shadow-2xl flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Search input */}
        <div className="flex items-center gap-2 px-3 py-2 border-b border-[#454545]">
          <File className="w-4 h-4 text-[#858585] shrink-0" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setSelectedIndex(0);
            }}
            onKeyDown={handleKeyDown}
            placeholder="Search files by name..."
            className="flex-1 bg-transparent text-sm text-[#d4d4d4] placeholder-[#858585] outline-none"
          />
          <kbd className="text-[9px] text-[#666] bg-[#1e1e1e] px-1.5 py-0.5 rounded border border-[#333]">
            ESC
          </kbd>
        </div>

        {/* File list */}
        <div ref={listRef} className="flex-1 overflow-y-auto py-1">
          {filtered.length === 0 ? (
            <div className="px-4 py-8 text-center text-xs text-[#858585]">
              {allFiles.length === 0 ? 'No files in workspace' : 'No matching files'}
            </div>
          ) : (
            filtered.map((file, idx) => {
              const Icon = getFileIcon(file.name);
              const colorClass = getExtColor(file.name);

              return (
                <button
                  key={file.path}
                  data-idx={idx}
                  onClick={() => handleSelect(file)}
                  className={cn(
                    'w-full flex items-center gap-2.5 px-3 py-1.5 text-left transition-colors',
                    idx === selectedIndex
                      ? 'bg-[#04395e] text-white'
                      : 'text-[#cccccc] hover:bg-[#2a2d2e]',
                  )}
                >
                  <Icon className={cn('w-4 h-4 shrink-0', colorClass)} />
                  <div className="flex-1 min-w-0 flex items-baseline gap-2">
                    <span className="text-xs font-medium truncate">{file.name}</span>
                    {file.directory && (
                      <span className="text-[10px] text-[#858585] truncate">{file.directory}</span>
                    )}
                  </div>
                </button>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-3 py-1 border-t border-[#454545] text-[9px] text-[#666]">
          <span>
            {filtered.length} file{filtered.length !== 1 ? 's' : ''}
          </span>
          <span>↑↓ Navigate · ↵ Open</span>
        </div>
      </div>
    </div>
  );
}
